import { environment } from '@/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { Actividad } from '@/app/models/interface/actividad.model';
import { Capacitacion } from '@/app/models/interface/capacitacion.model';
import { Competencia } from '@/app/models/interface/competencia.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { CustomResponse } from '@/app/models/interface/response.model';

export interface CustomDto {
  actividadesCompetencias: {
    actividadId: string;
    competenciasId: string[];
  }[];

  usuarioId: number;

  ccId: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class CapacitacionesService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'capacitaciones/';

  create(data: Partial<Capacitacion>) {
    return this.http.post<CustomResponse<Capacitacion>>(this.url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;

        const actividades = d.actividadCompetenciaCapacitaciones.reduce(
          (acc: Actividad[], curr) => {
            // Busca si la actividad ya está en el array acumulador
            const actividadExistente = acc.find(
              (a) => a.id === curr.actividadId,
            );

            if (actividadExistente) {
              // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
              if (
                !actividadExistente.competencias.some(
                  (c) => c.id === curr.competencia.id,
                )
              ) {
                actividadExistente.competencias.push(curr.competencia);
              }
            } else {
              // Si la actividad no existe, agrega una nueva entrada con sus competencias
              acc.push({
                ...curr.actividad,
                competencias: [curr.competencia],
              });
            }

            return acc;
          },
          [],
        );

        return {
          ...d,
          actividades,
          areas: d.areaCapacitaciones?.map((d) => d.area) || [],
        };
      }),
    );
  }

  findAll(
    params?: HttpParams,
  ): Observable<PaginatorModel<Capacitacion[], HasPagination>> {
    return this.http
      .get<
        CustomResponse<PaginatorModel<Capacitacion[], HasPagination>>
      >(this.url, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;

          if (d && Array.isArray(d.data)) {
            return {
              ...d,
              data: d.data.map((capacitacion) => {
                const actividades =
                  capacitacion.actividadCompetenciaCapacitaciones?.reduce(
                    (acc: Actividad[], curr) => {
                      // Busca si la actividad ya está en el array acumulador
                      const actividadExistente = acc.find(
                        (a) => a.id === curr.actividadId,
                      );

                      if (actividadExistente) {
                        // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
                        if (
                          !actividadExistente.competencias.some(
                            (c) => c.id === curr.competencia.id,
                          )
                        ) {
                          actividadExistente.competencias.push(
                            curr.competencia,
                          );
                        }
                      } else {
                        // Si la actividad no existe, agrega una nueva entrada con sus competencias
                        acc.push({
                          ...curr.actividad,
                          competencias: [curr.competencia],
                        });
                      }

                      return acc;
                    },
                    [],
                  ) || [];

                return {
                  ...capacitacion,
                  actividades,
                  areas:
                    capacitacion.areaCapacitaciones?.map((d) => ({
                      ...d.area,
                      tiempoEnMin: d.tiempoEnMin,
                    })) || [],
                };
              }),
            };
          }
          return d;
        }),
      );
  }

  findOne(id: string) {
    const url = this.url + id;
    return this.http.get<CustomResponse<Capacitacion>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          const actividades = d.actividadCompetenciaCapacitaciones.reduce(
            (acc: Actividad[], curr) => {
              // Busca si la actividad ya está en el array acumulador
              const actividadExistente = acc.find(
                (a) => a.id === curr.actividadId,
              );

              if (actividadExistente) {
                // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
                if (
                  !actividadExistente.competencias.some(
                    (c) => c.id === curr.competencia.id,
                  )
                ) {
                  actividadExistente.competencias.push(curr.competencia);
                }
              } else {
                // Si la actividad no existe, agrega una nueva entrada con sus competencias
                acc.push({
                  ...curr.actividad,
                  competencias: [curr.competencia],
                });
              }

              return acc;
            },
            [],
          );
          return {
            ...d,
            actividades,
            areas: d.areaCapacitaciones?.map((d) => d.area) || [],
          };
        }
        return d;
      }),
    );
  }

  update(capacitacion: Partial<Capacitacion>) {
    const url = this.url + capacitacion.id;
    return this.http.put<CustomResponse<Capacitacion>>(url, capacitacion).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          const actividades = d.actividadCompetenciaCapacitaciones.reduce(
            (acc: Actividad[], curr) => {
              // Busca si la actividad ya está en el array acumulador
              const actividadExistente = acc.find(
                (a) => a.id === curr.actividadId,
              );

              if (actividadExistente) {
                // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
                if (
                  !actividadExistente.competencias.some(
                    (c) => c.id === curr.competencia.id,
                  )
                ) {
                  actividadExistente.competencias.push(curr.competencia);
                }
              } else {
                // Si la actividad no existe, agrega una nueva entrada con sus competencias
                acc.push({
                  ...curr.actividad,
                  competencias: [curr.competencia],
                });
              }

              return acc;
            },
            [],
          );
          return {
            ...d,
            actividades,
            areas: d.areaCapacitaciones?.map((d) => d.area) || [],
          };
        }
        return d;
      }),
    );
  }

  changeState(state: boolean, id: string) {
    const url = this.url + id;
    return this.http.patch<CustomResponse<Capacitacion>>(url, { state }).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          const actividades = d.actividadCompetenciaCapacitaciones.reduce(
            (acc: Actividad[], curr) => {
              // Busca si la actividad ya está en el array acumulador
              const actividadExistente = acc.find(
                (a) => a.id === curr.actividadId,
              );

              if (actividadExistente) {
                // Si la actividad ya existe, agrega la competencia al array de competencias si no está duplicada
                if (
                  !actividadExistente.competencias.some(
                    (c) => c.id === curr.competencia.id,
                  )
                ) {
                  actividadExistente.competencias.push(curr.competencia);
                }
              } else {
                // Si la actividad no existe, agrega una nueva entrada con sus competencias
                acc.push({
                  ...curr.actividad,
                  competencias: [curr.competencia],
                });
              }

              return acc;
            },
            [],
          );
          return {
            ...d,
            actividades,
            areas: d.areaCapacitaciones?.map((d) => d.area) || [],
          };
        }
        return d;
      }),
    );
  }

  // TODO para las capacitaciones faltantes
  findByCompetencias(ids: CustomDto) {
    const url = this.url + 'find/competencias/';
    return this.http.post<CustomResponse<Capacitacion[]>>(url, ids).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        return data.result;
      }),
    );
  }

  getMatriz() {
    const dir = this.url + 'getMatriz';
    return this.http
      .get<
        CustomResponse<{
          capacitaciones: Capacitacion[];
          competencias: Competencia[];
        }>
      >(dir)
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }
}
